import { useState } from 'react';
import { Hand, Play, Shield } from 'lucide-react';

import { cn } from '../../../lib/cn';
import { useToast } from '../../../components/Toast';
import {
  useBlockPhone,
  useReleaseConversation,
  useTakeoverConversation,
} from '../botQueries';
import {
  BLOCK_REASON_LABELS_HE,
  isHumanTakeoverState,
  type BlockReason,
  type BotConversationRow,
} from '../bot.schema';
import styles from './BotActionsBar.module.css';

interface BotActionsBarProps {
  orgId: string | null;
  conversation: BotConversationRow;
}

export function BotActionsBar({ orgId, conversation }: BotActionsBarProps) {
  const toast = useToast();
  const takeover = useTakeoverConversation(orgId);
  const release = useReleaseConversation(orgId);
  const blockPhone = useBlockPhone(orgId);

  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockReason, setBlockReason] = useState<BlockReason>('manual');
  const [blockNotes, setBlockNotes] = useState('');

  const inTakeover = isHumanTakeoverState(conversation.state);
  const busy = takeover.isPending || release.isPending || blockPhone.isPending;

  async function onTakeover() {
    try {
      await takeover.mutateAsync({ id: conversation.id, currentState: conversation.state });
      toast.success('הבוט הושתק · אתה מנהל את השיחה');
    } catch {
      toast.error('לא הצלחנו להשתלט · נסה שוב');
    }
  }

  async function onRelease() {
    try {
      await release.mutateAsync({ id: conversation.id, currentState: conversation.state });
      toast.success('הבוט חזר לפעולה');
    } catch {
      toast.error('לא הצלחנו לשחרר · נסה שוב');
    }
  }

  async function onConfirmBlock() {
    try {
      await blockPhone.mutateAsync({
        phone: conversation.phone,
        reason: blockReason,
        ...(blockNotes.trim() ? { notes: blockNotes.trim() } : {}),
      });
      toast.success('המספר נחסם');
      setBlockModalOpen(false);
      setBlockNotes('');
    } catch {
      toast.error('החסימה נכשלה · נסה שוב');
    }
  }

  return (
    <>
      <div className={styles.bar}>
        {inTakeover ? (
          <button
            type="button"
            className={cn(styles.btn, styles.btnRelease)}
            disabled={busy}
            onClick={() => { void onRelease(); }}
          >
            <Play size={14} aria-hidden="true" />
            שחרר לבוט
          </button>
        ) : (
          <button
            type="button"
            className={cn(styles.btn, styles.btnTakeover)}
            disabled={busy}
            onClick={() => { void onTakeover(); }}
          >
            <Hand size={14} aria-hidden="true" />
            השתלט
          </button>
        )}

        <button
          type="button"
          className={cn(styles.btn, styles.btnDanger)}
          disabled={busy}
          onClick={() => { setBlockModalOpen(true); }}
        >
          <Shield size={14} aria-hidden="true" />
          חסום מספר
        </button>

        <span className={styles.disabledHint}>
          הודעה ידנית ותבניות — בקרוב
        </span>
      </div>

      {blockModalOpen ? (
        <div
          className={styles.modalBackdrop}
          onClick={() => { setBlockModalOpen(false); }}
        >
          <div
            className={styles.modal}
            onClick={(e) => { e.stopPropagation(); }}
          >
            <span className={styles.modalTitle}>חסום את המספר?</span>

            <div className={styles.modalField}>
              <label htmlFor="block-reason" className={styles.modalLabel}>סיבה</label>
              <select
                id="block-reason"
                className={styles.modalSelect}
                value={blockReason}
                onChange={(e) => { setBlockReason(e.target.value as BlockReason); }}
              >
                <option value="manual">{BLOCK_REASON_LABELS_HE.manual}</option>
                <option value="spam">{BLOCK_REASON_LABELS_HE.spam}</option>
                <option value="stop_keyword">{BLOCK_REASON_LABELS_HE.stop_keyword}</option>
              </select>
            </div>

            <div className={styles.modalField}>
              <label htmlFor="block-notes" className={styles.modalLabel}>הערות (לא חובה)</label>
              <textarea
                id="block-notes"
                className={styles.modalTextarea}
                value={blockNotes}
                onChange={(e) => { setBlockNotes(e.target.value); }}
                maxLength={1000}
              />
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.btn}
                onClick={() => { setBlockModalOpen(false); }}
              >
                בטל
              </button>
              <button
                type="button"
                className={cn(styles.btn, styles.btnDanger)}
                disabled={busy}
                onClick={() => { void onConfirmBlock(); }}
              >
                חסום
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
